import { TaskStatus, TaskType } from './../types/enum-type';
import { Task } from "@/types";
import { getData, postData, putData, deleteData } from "./axios-service"


function taskMapper(inputTask: any): Task {
    return {
        id: inputTask.id,
        taskType: inputTask.taskType,
        taskStatus: inputTask.taskStatus,
        name: inputTask.name,
        subject: inputTask.subject,
        observation: inputTask.observation,
        numeroDeContrat: inputTask.numeroDeContrat,
        toBeDone: inputTask.toBeDone,
        laverieId: inputTask.laverieId,
        taskListId: inputTask.taskListId,
        taskListCodeTournee: inputTask.taskListCodeTournee,
        priority: inputTask.priority,
        rank: inputTask.rank,
        number: inputTask.number,
        street: inputTask.street,
        postcode: inputTask.postcode,
        city: inputTask.city,
        latitude: inputTask.latitude,
        longitude: inputTask.longitude,
        startDateTime: inputTask.startDateTime,
        endDateTime: inputTask.endDateTime,
        subtasks: inputTask.subtasks || [],
        productId: inputTask.productId,
        productAquaNumber: inputTask.productAquaNumber,
        products: inputTask.products || []
    };
}

export function taskMapperArray(inputArray: any[]): Task[] {
    return inputArray.map(taskMapper);
}

export async function getFilteredTasks(taskStatuses: TaskStatus[], taskTypes: TaskType[]): Promise<Task[]> {
    const params = new URLSearchParams();

    if (taskStatuses.length > 0) {
        params.set("task_statuses", taskStatuses.join(","));
    }

    if (taskTypes.length > 0) {
        params.set("task_types", taskTypes.join(","));
    }

    const query = params.toString();
    const url = query ? `tasks?${query}` : "tasks";

    const response = await getData<Task[]>(url);
    return taskMapperArray(response ?? []);
}

export async function getTasksByTaskListId(taskListId: string): Promise<Task[]> {
    const url = `/tasks?taskListId=${taskListId}`;
    const response = await getData<Task[]>(url);
    return taskMapperArray(response ?? []);
}

export function getTaskById(id: string): Promise<Task> {
    return getData("/tasks/" + id).then(
        onSucceed => {
            return taskMapper(onSucceed);
        }
    );
}

export function fetchTaskByPanne(panneId: string): Promise<Task | null> {
    return getData("/pannes/" + panneId + "/task").then(
        onSucceed => {
            if (!onSucceed) {
                return null;
            }
            return taskMapper(onSucceed);
        }
    );
}

export function postTask(task: Task): Promise<Task> {
    return postData("/tasks", task).then(
        onSucceed => {
            return taskMapper(onSucceed);
        }
    );
}

export function putTask(task: Task): Promise<any> {
    return putData("/tasks/" + task.id, task)
}

export function updateTaskRank(task: Task): Promise<any> {
    return putData("/tasks/" + task.id + "/rank", { rank: task.rank })
}

export function setStartDateTime(taskId: string, startDateTime: Date): Promise<any> {
    return putData("/tasks/" + taskId + "/start-date-time", { startDateTime });
}

export function setEndDateTime(taskId: string, endDateTime: Date): Promise<any> {
    return putData("/tasks/" + taskId + "/end-date-time", { endDateTime })
}

export function deleteTask(ids: string[]): Promise<any> {
    const idsStr = ids.join(',');
    return deleteData("/tasks/" + idsStr)
}

export function sendReport(taskId: string): Promise<any> {
    return postData("/tasks/send-report", { taskId });
}

export async function searchForTasks(searchQuery: string): Promise<Task[]> {
    const response = await getData<Task[]>("tasks?" + searchQuery);
    return taskMapperArray(response ?? []);
}

// Records the result of the preparation control of one product of a preparation task
export function controlPreparation(taskId: string, productId: string, approved: boolean): Promise<any> {
    return putData("/tasks/" + taskId + "/products/" + productId + "/preparation-control", { approved });
}

// Takes a product assigned by mistake out of the preparation: it goes back to stock.
// The preparation task is deleted with its last product.
export async function removeProductFromPreparation(
    taskId: string, productId: string): Promise<{ taskDeleted: boolean }> {
    const result = await deleteData<{ taskDeleted: boolean }>("/tasks/" + taskId + "/products/" + productId);
    return result ?? { taskDeleted: false };
}

// Adds products to a preparation or removal task that has not been started yet
export function addProductsToTask(taskId: string, productIds: string[]): Promise<any> {
    return postData("/tasks/" + taskId + "/products", { productIds });
}

// Moves a product that could not be handled to the next open task of the same type in the laverie
export function postponeProduct(taskId: string, productId: string): Promise<any> {
    return postData("/tasks/" + taskId + "/products/" + productId + "/postpone", {});
}
